import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { format, addDays, isBefore, startOfDay, getDay, isSameDay, parseISO } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { CheckCircle2 } from "lucide-react";
import slotifyLogoSrc from "@/assets/slotify-logo.svg";
import AddToCalendar from "@/components/AddToCalendar";

interface SelfServiceSettings {
  allow_cancel: boolean;
  allow_rebook: boolean;
  cancel_deadline_hours: number;
  rebook_deadline_hours: number;
  show_in_shop: boolean;
  show_in_email: boolean;
  allowed_statuses: string[];
  rebook_notify_customer: boolean;
  rebook_notify_organisation: boolean;
  cancel_notify_customer: boolean;
  cancel_notify_organisation: boolean;
}

const ManageBooking = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const { t, language } = useLanguage();
  const { toast } = useToast();

  const [resNumber, setResNumber] = useState("");
  const [email, setEmail] = useState("");
  const [reservation, setReservation] = useState<any>(null);
  const [service, setService] = useState<any>(null);
  const [org, setOrg] = useState<any>(null);
  const [settings, setSettings] = useState<SelfServiceSettings | null>(null);
  const [extensionEnabled, setExtensionEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [looked, setLooked] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  // Rebook state
  const [rebooking, setRebooking] = useState(false);
  const [rebookDate, setRebookDate] = useState<Date | undefined>();
  const [rebookTime, setRebookTime] = useState<string>("");
  const [rebooked, setRebooked] = useState(false);
  const [rebookLoading, setRebookLoading] = useState(false);

  // Availability data for rebook
  const [openingHours, setOpeningHours] = useState<any[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<any[]>([]);
  const [existingReservations, setExistingReservations] = useState<{ date: string; duration: number }[]>([]);

  // Load extension settings + org data
  useEffect(() => {
    if (!shopId) return;
    const load = async () => {
      const [{ data: ext }, { data: orgData }] = await Promise.all([
        supabase
          .rpc("get_public_extension_info", { _organisation_id: shopId, _extension_id: "self-service" })
          .maybeSingle(),
        supabase
          .rpc("get_public_org_info", { _org_id: shopId })
          .single(),
      ]);

      if (orgData) setOrg(orgData);

      if (ext && (ext as any).enabled) {
        setExtensionEnabled(true);
        const defaults: SelfServiceSettings = {
          allow_cancel: true,
          allow_rebook: true,
          cancel_deadline_hours: 24,
          rebook_deadline_hours: 24,
          show_in_shop: true,
          show_in_email: true,
          allowed_statuses: ["pending", "confirmed"],
          rebook_notify_customer: true,
          rebook_notify_organisation: true,
          cancel_notify_customer: true,
          cancel_notify_organisation: true,
        };
        setSettings({ ...defaults, ...(ext as any).settings });
      }
    };
    load();
  }, [shopId]);

  const lookup = async () => {
    if (!resNumber.trim() || !email.trim() || !shopId) return;
    setLoading(true);
    setLooked(true);
    setReservation(null);

    try {
      const { data, error } = await supabase.functions.invoke("manage-booking", {
        body: {
          action: "lookup",
          reservation_number: resNumber.trim().toUpperCase(),
          organisation_id: shopId,
          email: email.trim(),
        },
      });

      if (error || data?.error) {
        // Not found
      } else if (data?.reservation) {
        setReservation(data.reservation);
        setService({ name: data.reservation.service_name });
      }
    } catch {
      // lookup failed
    }

    setLoading(false);
  };

  const isStatusAllowed = () => {
    if (!reservation || !settings) return false;
    const allowed = settings.allowed_statuses || ["pending", "confirmed"];
    return allowed.includes(reservation.status);
  };

  const canCancel = () => {
    if (!settings?.allow_cancel || !reservation) return false;
    if (!isStatusAllowed()) return false;
    const bookingDate = new Date(reservation.date);
    const deadline = new Date(bookingDate.getTime() - settings.cancel_deadline_hours * 60 * 60 * 1000);
    return new Date() < deadline;
  };

  const canRebook = () => {
    if (!settings?.allow_rebook || !reservation) return false;
    if (!isStatusAllowed()) return false;
    const bookingDate = new Date(reservation.date);
    const deadline = new Date(bookingDate.getTime() - settings.rebook_deadline_hours * 60 * 60 * 1000);
    return new Date() < deadline;
  };

  const handleCancel = async () => {
    if (!reservation) return;
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-booking", {
        body: {
          reservation_id: reservation.id,
          email: email.trim(),
          action: "cancel",
          notify_customer: settings?.cancel_notify_customer ?? true,
          notify_organisation: settings?.cancel_notify_organisation ?? true,
          language,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCancelled(true);
    } catch (err: any) {
      toast({ title: err.message || "Failed to cancel", variant: "destructive" });
    }
    setCancelling(false);
  };

  // Fetch availability data when rebooking starts
  const fetchAvailability = useCallback(async () => {
    if (!shopId || !reservation) return;
    const [hoursRes, blockedRes, reservationsRes] = await Promise.all([
      supabase.from("opening_hours").select("day_of_week, is_open, open_time, close_time").eq("organisation_id", shopId),
      supabase.from("blocked_times").select("start_date, end_date, start_time, end_time, type").eq("organisation_id", shopId).eq("active", true),
      supabase.from("reservations").select("date, duration").eq("organisation_id", shopId).in("status", ["pending", "confirmed", "awaiting_payment"]).neq("id", reservation.id),
    ]);
    setOpeningHours(hoursRes.data || []);
    setBlockedTimes(blockedRes.data || []);
    setExistingReservations((reservationsRes.data || []).map((r: any) => ({ date: r.date, duration: r.duration })));
  }, [shopId, reservation]);

  useEffect(() => {
    if (rebooking) fetchAvailability();
  }, [rebooking, fetchAvailability]);

  const jsToDbDay = (jsDay: number) => (jsDay === 0 ? 6 : jsDay - 1);

  const closedDays = useMemo(() => {
    return openingHours.filter((h: any) => !h.is_open).map((h: any) => h.day_of_week);
  }, [openingHours]);

  const fullDayBlockedDates = useMemo(() => {
    const dates: Date[] = [];
    const parseLocalDate = (d: string) => {
      const [y, m, day] = d.split("-").map(Number);
      return new Date(y, m - 1, day);
    };
    blockedTimes.forEach((bt: any) => {
      if (bt.type === "datetime_range" && bt.start_time && bt.end_time && bt.end_date && bt.start_date !== bt.end_date) {
        let cur = addDays(parseLocalDate(bt.start_date), 1);
        const end = addDays(parseLocalDate(bt.end_date), -1);
        while (cur <= end) { dates.push(new Date(cur)); cur = addDays(cur, 1); }
        return;
      }
      if (bt.start_time && bt.end_time) return;
      if (bt.type === "single_day" || !bt.end_date) {
        dates.push(parseLocalDate(bt.start_date));
      } else {
        let cur = parseLocalDate(bt.start_date);
        const end = parseLocalDate(bt.end_date);
        while (cur <= end) { dates.push(new Date(cur)); cur = addDays(cur, 1); }
      }
    });
    return dates;
  }, [blockedTimes]);

  const isSlotBlocked = (date: Date, slotStart: number, slotEnd: number): boolean => {
    return blockedTimes.some((bt: any) => {
      if (!bt.start_time || !bt.end_time) return false;
      const [sy, sm, sd] = bt.start_date.split("-").map(Number);
      const btStartDate = new Date(sy, sm - 1, sd);
      const [ey, em, ed] = bt.end_date ? bt.end_date.split("-").map(Number) : [sy, sm, sd];
      const btEndDate = new Date(ey, em - 1, ed);
      const dateDay = startOfDay(date);
      if (dateDay < btStartDate || dateDay > btEndDate) return false;
      const [bsH, bsM] = bt.start_time.split(":").map(Number);
      const [beH, beM] = bt.end_time.split(":").map(Number);
      const blockStart = bsH * 60 + bsM;
      const blockEnd = beH * 60 + beM;
      if (bt.type === "datetime_range" && bt.end_date && bt.start_date !== bt.end_date) {
        const isFirstDay = dateDay.getTime() === btStartDate.getTime();
        const isLastDay = dateDay.getTime() === btEndDate.getTime();
        const isMiddleDay = dateDay > btStartDate && dateDay < btEndDate;
        if (isMiddleDay) return true;
        if (isFirstDay && isLastDay) {
          if (blockStart >= blockEnd) return true;
          return slotStart < blockEnd && slotEnd > blockStart;
        }
        if (isFirstDay) return slotEnd > blockStart;
        if (isLastDay) return slotStart < blockEnd;
        return false;
      }
      if (blockStart >= blockEnd) return false;
      return slotStart < blockEnd && slotEnd > blockStart;
    });
  };

  const isSlotBooked = (date: Date, slotStart: number, slotEnd: number): boolean => {
    return existingReservations.some((r) => {
      const rDate = parseISO(r.date);
      if (!isSameDay(rDate, date)) return false;
      const rStart = rDate.getHours() * 60 + rDate.getMinutes();
      const rEnd = rStart + r.duration;
      return slotStart < rEnd && slotEnd > rStart;
    });
  };

  const getAvailableSlots = (date: Date, duration: number): string[] => {
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = jsToDbDay(getDay(localDate));
    const dayHours = openingHours.find((h: any) => h.day_of_week === dayOfWeek && h.is_open);
    if (!dayHours) return [];
    const openH = parseInt(dayHours.open_time.split(":")[0]);
    const openM = parseInt(dayHours.open_time.split(":")[1]);
    const closeH = parseInt(dayHours.close_time.split(":")[0]);
    const closeM = parseInt(dayHours.close_time.split(":")[1]);
    const closeMinutes = closeH * 60 + closeM;
    const now = new Date();
    const minLeadMinutes = org?.booking_min_lead_time || 0;
    const snapMinToDay = org?.booking_min_lead_snap_to_day || false;
    let earliestBookable = new Date(now.getTime() + minLeadMinutes * 60 * 1000);
    if (snapMinToDay && minLeadMinutes > 0) {
      if (startOfDay(earliestBookable).getTime() > startOfDay(now).getTime()) {
        earliestBookable = startOfDay(earliestBookable);
      }
    }
    let latestBookable: Date | null = null;
    const maxAdvanceMinutes = (org?.booking_max_advance_time != null && org.booking_max_advance_time > 0) ? org.booking_max_advance_time : null;
    const snapMaxToDay = org?.booking_max_advance_snap_to_day || false;
    if (maxAdvanceMinutes) {
      latestBookable = new Date(now.getTime() + maxAdvanceMinutes * 60 * 1000);
      if (snapMaxToDay) {
        latestBookable = new Date(startOfDay(latestBookable).getTime() + 24 * 60 * 60 * 1000 - 1);
      }
    }
    const slotStep = org?.booking_slot_interval || 30;
    const slots: string[] = [];
    let m = openH * 60 + openM;
    while (m + duration <= closeMinutes) {
      const slotDate = new Date(localDate);
      slotDate.setHours(Math.floor(m / 60), m % 60, 0, 0);
      if (slotDate < earliestBookable) { m += slotStep; continue; }
      if (latestBookable && slotDate > latestBookable) { m += slotStep; continue; }
      if (!isSlotBlocked(localDate, m, m + duration) && !isSlotBooked(localDate, m, m + duration)) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        slots.push(`${h.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`);
      }
      m += slotStep;
    }
    return slots;
  };

  const isDateDisabled = (date: Date) => {
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = startOfDay(new Date());
    if (isBefore(localDate, today)) return true;
    const dow = jsToDbDay(getDay(localDate));
    if (closedDays.includes(dow)) return true;
    if (fullDayBlockedDates.some((d) => d.getTime() === localDate.getTime())) return true;
    if (org?.booking_max_advance_time != null && org.booking_max_advance_time > 0) {
      let maxDate = new Date(new Date().getTime() + org.booking_max_advance_time * 60 * 1000);
      if (org.booking_max_advance_snap_to_day) {
        maxDate = new Date(startOfDay(maxDate).getTime() + 24 * 60 * 60 * 1000 - 1);
      }
      if (localDate > startOfDay(addDays(maxDate, 1))) return true;
    }
    const duration = reservation?.duration || 30;
    const slots = getAvailableSlots(localDate, duration);
    if (slots.length === 0) return true;
    return false;
  };

  const rebookDuration = reservation?.duration || 30;

  const timeSlots = useMemo(() => {
    if (!rebookDate) return [];
    const localDate = new Date(rebookDate.getFullYear(), rebookDate.getMonth(), rebookDate.getDate());
    return getAvailableSlots(localDate, rebookDuration);
  }, [rebookDate, rebookDuration, openingHours, blockedTimes, existingReservations]);

  // Store the confirmed rebook date/time for the confirmation screen
  const [confirmedRebookDate, setConfirmedRebookDate] = useState<Date | undefined>();
  const [confirmedRebookTime, setConfirmedRebookTime] = useState<string>("");

  const handleRebook = async () => {
    if (!rebookDate || !rebookTime || !reservation) return;
    setRebookLoading(true);
    const dateStr = format(rebookDate, "yyyy-MM-dd");
    const newDate = `${dateStr}T${rebookTime}:00`;

    try {
      const { data, error } = await supabase.functions.invoke("manage-booking", {
        body: {
          reservation_id: reservation.id,
          email: email.trim(),
          action: "rebook",
          new_date: newDate,
          notify_customer: settings?.rebook_notify_customer ?? true,
          notify_organisation: settings?.rebook_notify_organisation ?? true,
          language,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setConfirmedRebookDate(rebookDate);
      setConfirmedRebookTime(rebookTime);
      setRebooked(true);
      setRebooking(false);
    } catch (err: any) {
      toast({ title: err.message || "Failed to rebook", variant: "destructive" });
    }
    setRebookLoading(false);
  };

  const getStatusLabel = () => {
    if (!reservation) return "";
    const statusMap: Record<string, string> = {
      pending: t("reservations.status_pending"),
      confirmed: t("reservations.status_confirmed"),
      cancelled: t("reservations.status_cancelled"),
      completed: t("reservations.status_completed"),
      no_show: t("reservations.status_no_show"),
    };
    return statusMap[reservation.status] || reservation.status;
  };

  if (!extensionEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="text-center text-muted-foreground text-sm">
          {t("ext.ss_not_available")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className={cn("w-full space-y-6", rebooking ? "max-w-2xl" : "max-w-md")}>
        <div className="text-center flex flex-col items-center">
          {(() => {
            const logoUrl = org?.logo_light_url;
            const sizeClass = { small: "h-8", medium: "h-12", large: "h-16", xlarge: "h-20" }[org?.logo_size || "medium"] || "h-12";
            return logoUrl ? (
              <img src={logoUrl} alt={org?.name || ""} className={`${sizeClass} mb-4 object-contain`} />
            ) : null;
          })()}
          <h1 className="text-xl font-bold text-foreground">{t("ext.ss_manage_title")}</h1>
          {org && <p className="text-sm text-muted-foreground mt-1">{org.name}</p>}
        </div>

        {cancelled ? (
          <div className="rounded-xl border border-border bg-card p-6 text-center">
            <p className="text-foreground font-semibold mb-1">{t("ext.ss_cancelled_title")}</p>
            <p className="text-sm text-muted-foreground">{t("ext.ss_cancelled_desc")}</p>
          </div>
        ) : rebooked ? (
          <div className="rounded-xl border border-border bg-card p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
              <CheckCircle2 className="h-8 w-8 text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t("ext.ss_rebooked_title")}</h2>
            {reservation?.reservation_number && (
              <p className="mt-1 font-mono text-sm text-muted-foreground">{reservation.reservation_number}</p>
            )}
            <p className="mt-2 text-muted-foreground">{t("ext.ss_rebooked_desc")}</p>
            <div className="mx-auto mt-6 max-w-sm rounded-xl bg-muted/50 p-4 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("shop.service")}</span>
                  <span className="font-medium text-foreground">{service?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("shop.date_time")}</span>
                  <span className="font-medium text-foreground">
                    {confirmedRebookDate && format(confirmedRebookDate, "EEEE, MMM d, yyyy")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("ext.ss_time")}</span>
                  <span className="font-medium text-foreground">{confirmedRebookTime}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="text-muted-foreground">{t("ext.ss_duration")}</span>
                  <span className="font-medium text-foreground">{reservation?.duration} min</span>
                </div>
              </div>
            </div>
            {confirmedRebookDate && confirmedRebookTime && service && (
              <AddToCalendar
                title={`${service.name}${org?.name ? ` — ${org.name}` : ""}`}
                date={confirmedRebookDate}
                time={confirmedRebookTime}
                durationMinutes={reservation?.duration || 30}
                location={org?.name}
              />
            )}
          </div>
        ) : reservation && !rebooking ? (
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div>
              <p className="text-xs text-muted-foreground">{t("ext.ss_reservation_number")}</p>
              <p className="text-sm font-semibold text-foreground">{reservation.reservation_number}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("ext.ss_service")}</p>
              <p className="text-sm text-foreground">{service?.name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("ext.ss_datetime")}</p>
              <p className="text-sm text-foreground">
                {format(new Date(reservation.date), "dd-MM-yyyy HH:mm")}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("ext.ss_status")}</p>
              <p className="text-sm text-foreground">{getStatusLabel()}</p>
            </div>

            <div className="flex gap-2 pt-2">
              {canRebook() && (
                <Button variant="outline" className="flex-1" onClick={() => { setRebooking(true); setRebookDate(undefined); setRebookTime(""); }}>
                  {t("ext.ss_rebook_btn")}
                </Button>
              )}
              {canCancel() && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="flex-1">
                      {t("ext.ss_cancel_btn")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("ext.ss_cancel_confirm_title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("ext.ss_cancel_confirm_desc")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("ext.ss_keep")}</AlertDialogCancel>
                      <AlertDialogAction onClick={handleCancel} disabled={cancelling}>
                        {cancelling ? t("ext.ss_cancelling") : t("ext.ss_confirm_cancel")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            {!canCancel() && !canRebook() && isStatusAllowed() && (
              <p className="text-xs text-muted-foreground text-center">{t("ext.ss_deadline_passed")}</p>
            )}
            {!isStatusAllowed() && (
              <p className="text-xs text-muted-foreground text-center">{t("ext.ss_status_not_allowed")}</p>
            )}
          </div>
        ) : rebooking ? (
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">{t("ext.ss_pick_new_datetime")}</h2>
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="flex-1">
                <Calendar
                  mode="single"
                  selected={rebookDate}
                  onSelect={(d) => {
                    setRebookDate(d);
                    setRebookTime("");
                  }}
                  disabled={isDateDisabled}
                  weekStartsOn={1}
                  className="p-3 pointer-events-auto rounded-xl border border-border"
                  initialFocus
                />
              </div>
              {rebookDate ? (
                <div className="flex-1 max-h-[400px] overflow-y-auto">
                  <p className="text-sm font-medium text-foreground mb-3">
                    {t("shop.available_times")} {format(rebookDate, "EEEE, MMM d")}
                  </p>
                  {timeSlots.length === 0 ? (
                    <div className="flex items-center justify-center rounded-xl border border-dashed border-border py-8 px-4">
                      <p className="text-sm text-muted-foreground text-center">{t("shop.no_timeslots")}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Available time slots">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setRebookTime(slot)}
                          aria-pressed={rebookTime === slot}
                          aria-label={`Time slot ${slot}`}
                          className={cn(
                            "rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                            rebookTime === slot
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-foreground hover:border-primary/30 hover:bg-primary/5"
                          )}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center rounded-xl border border-dashed border-border py-8 px-4">
                  <p className="text-sm text-muted-foreground text-center">{t("shop.no_timeslots")}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
              <Button variant="ghost" onClick={() => setRebooking(false)}>
                {t("shop.back")}
              </Button>
              <Button onClick={handleRebook} disabled={!rebookTime || rebookLoading}>
                {rebookLoading ? t("ext.ss_rebooking") : t("ext.ss_confirm_rebook")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">{t("ext.ss_reservation_number")}</Label>
              <Input
                placeholder="RES-2026-00001"
                value={resNumber}
                onChange={(e) => setResNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("shop.email")}</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button onClick={lookup} disabled={loading || !resNumber.trim() || !email.trim()} className="w-full">
              {loading ? t("ext.ss_looking_up") : t("ext.ss_lookup")}
            </Button>
            {looked && !reservation && !loading && (
              <p className="text-xs text-destructive text-center">{t("ext.ss_not_found")}</p>
            )}
          </div>
        )}

        {/* Powered by Slotify footer */}
        <div className="mt-4 text-center">
          <a
            href="https://slotify.nl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            Powered by
            <img src={slotifyLogoSrc} alt="Slotify" className="h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ManageBooking;
